import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Type,
  IconChartUp,
  IconChartDown,
  Image,
} from '@spotify-internal/encore-creator-web';
import {
  screenXxsMax,
  screenXsMin,
  screenXsMax,
  screenSmMin,
  screenSmMax,
  screenMdMin,
  screenLgMax,
  screenXlMin,
  spacer24,
  spacer48,
  gray95,
  success,
  failure,
} from '@spotify-internal/encore-foundation';

import { Screen } from '../../../../../shared/lib/useViewport';

const maxCroppedImageSize = '144px';
const defaultBackdropPreset = 'VIBRANT';

const ArtCardBackdrop = styled.div`
  background-color: ${props => props.backdropColor.color};
  opacity: ${props => props.backdropColor.opacity};
  height: calc(
    ${props => getViewportMultiplier(props.viewport)}vw / 2 + ${spacer24}
  );
  grid-column: 1;
  grid-row: 1;
  max-height: calc(${maxCroppedImageSize} / 2 + ${spacer24});
  min-height: ${spacer48};
`;

const ArtCardHead = styled.div`
  display: grid;
`;

const CardCroppedImage = styled(Image)`
  height: ${props => getViewportMultiplier(props.viewport)}vw;
  margin-left: ${spacer24};
  margin-top: ${spacer24};
  max-height: ${maxCroppedImageSize};
  max-width: ${maxCroppedImageSize};
  min-height: ${spacer48};
  min-width: ${spacer48};
  width: ${props => getViewportMultiplier(props.viewport)}vw;
`;

const ArtCardImage = styled(CardCroppedImage)`
  grid-column: 1;
  grid-row: 1;
  position: relative;
`;

const IconCardImage = styled(CardCroppedImage)`
  border-radius: 50%;
`;

const InfoCardImage = styled(Image)`
  max-height: 196px;
  width: 100%;

  @media (max-width: ${screenXxsMax}) {
    height: 36vw;
  }

  @media (min-width: ${screenXsMin}) and (max-width: ${screenXsMax}) {
    height: 32vw;
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}) {
    height: 19vw;
  }

  @media (min-width: ${screenMdMin}) and (max-width: ${screenLgMax}) {
    height: 14vw;
  }

  @media (min-width: ${screenXlMin}) {
    height: 10vw;
  }
`;

const StatCardArrowUp = styled(IconChartUp).attrs({
  iconSize: 24,
  color: success,
})`
  margin-bottom: -7px;
`;

const StatCardArrowDown = styled(IconChartDown).attrs({
  iconSize: 24,
  color: failure,
})`
  margin-bottom: -7px;
`;

const StatCardHead = styled(Type.h1).attrs({
  variant: Type.display2,
  weight: Type.bold,
  condensed: true,
})`
  padding: ${spacer24} ${spacer24} 0 ${spacer24};
`;

const getViewportMultiplier = viewport => {
  if (viewport <= Screen.XS) {
    return 30;
  }

  if (viewport > Screen.XS && viewport < Screen.SM) {
    return 24;
  }

  if (viewport >= Screen.SM && viewport < Screen.MD) {
    return 14;
  }

  if (viewport >= Screen.MD && viewport < Screen.LG) {
    return 12;
  }

  if (viewport >= Screen.LG && viewport < Screen.XL) {
    return 10;
  }

  // viewport >= Screen.XL
  return 8;
};

export const getBackdropColor = cardColorList => {
  let color = gray95;
  let opacity = 1.0;

  if (Array.isArray(cardColorList) && cardColorList.length > 0) {
    const cardColor = cardColorList.find(
      c => c.preset === defaultBackdropPreset,
    );
    if (cardColor) {
      color = `#${cardColor.colorHex}`;
      opacity = 0.3;
    }
  }
  return { color, opacity };
};

const CardHeader = React.memo(props => {
  const {
    displayType,
    header,
    fallbackImageUrl,
    cardColorList,
    viewport,
    headerType,
  } = props;
  const image = header ? header : fallbackImageUrl;

  const placeholder = fallbackImageUrl
    ? fallbackImageUrl
    : 'data:image/png;base64,R0lGODlhFAAUAIAAAP///wAAACH5BAEAAAAALAAAAAAUABQAAAIRhI+py+0Po5y02ouz3rz7rxUAOw==';

  const backdropColor = getBackdropColor(cardColorList);

  switch (displayType) {
    case 'COVER':
      return (
        <ArtCardHead>
          <ArtCardBackdrop viewport={viewport} backdropColor={backdropColor} />
          <ArtCardImage
            alt=""
            src={image}
            viewport={viewport}
            placeholderSrc={placeholder}
            crop
          />
        </ArtCardHead>
      );
    case 'STAT':
      if (headerType === 'DELTA') {
        const deltaAsNumber = Number(header);
        const isValidNumber = !isNaN(deltaAsNumber);

        return isValidNumber ? (
          <StatCardHead>
            {header}
            {deltaAsNumber > 0 ? <StatCardArrowUp /> : <StatCardArrowDown />}
          </StatCardHead>
        ) : null;
      }
      return <StatCardHead>{header}</StatCardHead>;
    case 'INFO':
      return (
        <InfoCardImage alt="" src={image} placeholderSrc={placeholder} crop />
      );
    case 'ICON':
      return (
        <IconCardImage
          alt=""
          src={image}
          viewport={viewport}
          placeholderSrc={placeholder}
          crop
        />
      );
    default:
      return null;
  }
});

CardHeader.propTypes = {
  displayType: PropTypes.oneOf([
    'COVER',
    'ICON',
    'INFO',
    'STAT',
    'UNKNOWN_DISPLAY_TYPE',
  ]).isRequired,
  headerType: PropTypes.oneOf([
    'IMAGE_URL',
    'TEXT',
    'DELTA',
    'UNKNOWN_HEADER_TYPE',
  ]).isRequired,
  header: PropTypes.string.isRequired,
  fallbackImageUrl: PropTypes.string,
  cardColorList: PropTypes.arrayOf(
    PropTypes.shape({
      preset: PropTypes.string,
      colorHex: PropTypes.string,
    }),
  ),
  viewport: PropTypes.number.isRequired,
};

/* eslint-disable-next-line import/no-default-export */
export default CardHeader;
